import React from "react";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../utils/utils";

/**
 * Customer Details Modal Component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Object} props.customer - Customer data to display
 * @param {Function} props.onClose - Handler for closing the modal
 * @param {Function} props.getCustomerStatusBadge - Function to get status badge class
 * @param {Function} props.getCustomerStatusText - Function to get status text
 */
const CustomerDetailsModal = ({
  isOpen,
  customer,
  onClose,
  getCustomerStatusBadge,
  getCustomerStatusText,
}) => {
  const { t } = useTranslation();

  if (!isOpen || !customer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">
            {t("customers.customerDetails")} - {customer.fullName}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded-full p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t("customers.customerName")}
                </label>
                <p className="text-base text-gray-900">{customer.fullName}</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t("customers.phone")}
                </label>
                <p className="font-mono text-gray-900">{customer.phone}</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t("customers.subscription")}
                </label>
                <p className="text-gray-900">{customer.subscriptionName}</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t("customers.status")}
                </label>
                <div className={getCustomerStatusBadge(customer)}>
                  {getCustomerStatusText(customer)}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t("customers.period")}
                </label>
                <p className="text-gray-900">
                  {formatDate(customer.startDate)} -{" "}
                  {formatDate(customer.endDate)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t("customers.remainingDays")}
                </label>
                <p className="text-lg font-bold text-gray-900">
                  {customer.remainingDaysCount || 0} {t("customers.days")}
                </p>
              </div>

              {customer.defaultLocation && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {t("customers.location")}
                  </label>
                  <div className="text-sm">
                    {customer.defaultLocation.city && (
                      <div className="text-gray-900">{customer.defaultLocation.city}</div>
                    )}
                    {customer.defaultLocation.address && (
                      <div className="text-gray-500">
                        {customer.defaultLocation.address}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {customer.isFrozenByUser && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <span className="text-yellow-800 font-medium">{t("customers.frozenMessage")}</span>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            onClick={onClose}
          >
            {t("customers.close")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailsModal;
